import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { useClientUser } from '@/hooks';
import { GET_CARRIER_PICKUP_LOCATION, GET_SELLABLE_SKU_ITEMS, GET_SELLABLE_SKUS } from './graphql/queries';
import { draftKey, groupItemsByDraftKey } from './utils';

export const useSellableSkus = ({ enabled = true } = {}) => {
    const { user_id } = useClientUser();
    const { data, loading, error } = useQuery(GET_SELLABLE_SKUS, {
        variables: { client_id: user_id },
        skip: !user_id || !enabled,
        fetchPolicy: 'cache-and-network',
        onError: (e) => {
            console.error(e);
            captureException(e);
        },
    });

    return {
        rows: data?.forward_stocking_sku_by_carrier || [],
        loading: enabled && (loading || !user_id),
        error,
    };
};

export const useCarrierPickupLocation = (carrierId) => {
    const { data, loading, error } = useQuery(GET_CARRIER_PICKUP_LOCATION, {
        variables: { carrier_id: carrierId },
        skip: !carrierId,
        fetchPolicy: 'no-cache',
        onError: (e) => {
            console.error(e);
            captureException(e);
        },
    });

    const locations = data?.locations || [];
    const carrierPhone = data?.clients?.[0]?.business_phone || null;
    return { location: locations[0] || null, carrierPhone, loading: loading && !data, error };
};

export const useOrderDraft = () => {
    const [draft, setDraft] = useState(() => new Map());

    const add = useCallback((row, { qty = 1 } = {}) => {
        setDraft((current) => {
            const next = new Map(current);
            next.set(draftKey(row), { ...row, qty, item_ids: [] });
            return next;
        });
    }, []);

    const setQty = useCallback((row, qty) => {
        setDraft((current) => {
            const next = new Map(current);
            const key = draftKey(row);
            if (qty <= 0) {
                next.delete(key);
            } else {
                next.set(key, { ...row, qty });
            }
            return next;
        });
    }, []);

    const setSerials = useCallback((row, item_ids) => {
        setDraft((current) => {
            const next = new Map(current);
            const key = draftKey(row);
            next.set(key, { ...(next.get(key) || row), item_ids, qty: item_ids.length });
            return next;
        });
    }, []);

    const remove = useCallback((row) => {
        setDraft((current) => {
            const next = new Map(current);
            next.delete(draftKey(row));
            return next;
        });
    }, []);

    const clear = useCallback(() => setDraft(new Map()), []);

    return { draft, add, setQty, setSerials, remove, clear };
};

export const useSellableSkuItems = (carrierId, skus, { enabled = true } = {}) => {
    const { user_id } = useClientUser();
    const active = enabled && !!user_id && !!carrierId && skus.length > 0;

    const { data, loading, error } = useQuery(GET_SELLABLE_SKU_ITEMS, {
        variables: { client_id: user_id, carrier_id: carrierId, skus },
        skip: !active,
        fetchPolicy: 'cache-and-network',
        onError: (e) => {
            console.error(e);
            captureException(e);
        },
    });

    const itemsByKey = useMemo(() => groupItemsByDraftKey(data?.forward_stocking_items), [data]);

    return { itemsByKey, loading: active && loading && !data, loaded: !active || !!data, error };
};
